// Grupo de promos del detalle del equipo («Regalo», «Descuento», «Beneficio», «Cupón», «Crédito»).
// Muestra hasta 3; «+N más» expande el grupo en el mismo panel. Sin promos no se pinta.
import { useState, type ReactNode } from 'react';
import { Ban, Check, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
    PROMOTION_GROUP_LIMIT,
    PROMOTION_GROUP_SELECTED,
    PROMOTION_GROUP_TONE,
    type PromotionGroupTone,
} from '../promotionConstants';

export interface PromotionGroupItem {
    key: string;
    node: ReactNode;
}

export function PromotionGroup({
    label,
    icon: Icon,
    tone,
    items,
    note,
    limit = PROMOTION_GROUP_LIMIT,
    hideLabel = false,
}: {
    label: string;
    /** Aclaración bajo el título, p. ej. «Según su nivel». */
    note?: string;
    /** Cuántas se ven antes de «+N más»; en un selector, todas. */
    limit?: number;
    icon: LucideIcon;
    tone: PromotionGroupTone;
    items: readonly PromotionGroupItem[];
    /**
     * Oculta el header con el ícono y el label. El color de la caja basta para identificar el
     * grupo (verde = descuento, morado = crédito, etc.). `aria-label` del `<section>` lo deja
     * accesible aunque no esté visible. Usado en el panel derecho de «Elige el teléfono» donde
     * la densidad importa.
     */
    hideLabel?: boolean;
}) {
    const [expanded, setExpanded] = useState(false);
    if (items.length === 0) return null;
    const shown = expanded ? items : items.slice(0, limit);
    const hidden = items.length - shown.length;
    const colors = PROMOTION_GROUP_TONE[tone];
    return (
        <section
            aria-label={label}
            className={cn('mt-3 flex flex-col gap-2 rounded-xl p-3 text-[11px]', colors.box)}
        >
            {!hideLabel && (
                <div
                    className={cn(
                        'flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.08em]',
                        colors.text,
                    )}
                >
                    <Icon className="size-3.5" aria-hidden />
                    {label}
                </div>
            )}
            {note && <p className="-mt-1 text-[10.5px] text-muted-foreground">{note}</p>}
            <ul className="flex list-none flex-col gap-2">
                {shown.map((item) => (
                    <li key={item.key}>{item.node}</li>
                ))}
            </ul>
            {hidden > 0 && (
                <button
                    type="button"
                    onClick={() => {
                        setExpanded(true);
                    }}
                    className={cn(
                        'self-start rounded-sm text-[11px] font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        colors.text,
                    )}
                >
                    +{hidden} más
                </button>
            )}
        </section>
    );
}

/** Texto plano para el tooltip de una línea truncada. */
function titleOf(node: ReactNode): string | undefined {
    return typeof node === 'string' ? node : undefined;
}

/**
 * Renglón de una promo, del mismo alto siempre: nombre, descripción y una línea de
 * condiciones; cada una truncada con tooltip y reservada aunque esté vacía.
 */
export function PromotionGroupRow({
    icon: Icon,
    tone,
    name,
    aside,
    description,
    meta,
    metaTone = 'muted',
}: {
    icon: LucideIcon;
    /** Sin tono, el ícono va gris (p. ej. «Ninguna»). */
    tone?: PromotionGroupTone;
    name: ReactNode;
    /** A la derecha del nombre (p. ej. chips de nivel). */
    aside?: ReactNode;
    /** Qué da, en una línea. */
    description?: ReactNode;
    /** Condiciones, ahorro o aviso, en una línea. */
    meta?: ReactNode;
    metaTone?: 'muted' | 'warning' | 'group';
}) {
    return (
        <div className="flex items-start gap-2">
            <Icon
                className={cn(
                    'mt-0.5 size-3.5 flex-none',
                    tone ? PROMOTION_GROUP_TONE[tone].text : 'text-muted-foreground',
                )}
                aria-hidden
            />
            <div className="min-w-0 flex-1">
                <div className="flex h-[18px] items-center justify-between gap-2">
                    <span
                        className="min-w-0 truncate text-[12px] font-bold leading-[18px]"
                        title={titleOf(name)}
                    >
                        {name}
                    </span>
                    {aside}
                </div>
                <div
                    data-line="description"
                    className="h-4 truncate leading-4 text-muted-foreground"
                    title={titleOf(description)}
                >
                    {description}
                </div>
                <div
                    data-line="meta"
                    className={cn(
                        'h-4 truncate text-[10.5px] leading-4',
                        metaTone === 'warning'
                            ? 'font-semibold text-warning'
                            : metaTone === 'group' && tone
                              ? cn('font-semibold', PROMOTION_GROUP_TONE[tone].text)
                              : 'text-muted-foreground',
                    )}
                    title={titleOf(meta)}
                >
                    {meta}
                </div>
            </div>
        </div>
    );
}

/** Renglón elegible con el marcador de selección (radio o casilla) y el renglón de la promo. */
export function SelectablePromotionRow({
    role,
    checked,
    disabled,
    pending = false,
    onSelect,
    tone,
    children,
}: {
    role: 'radio' | 'checkbox';
    checked: boolean;
    disabled: boolean;
    pending?: boolean;
    onSelect?: () => void;
    /** Color del grupo para marcar la elegida; sin él, el de la marca. */
    tone?: PromotionGroupTone;
    children: ReactNode;
}) {
    const selected = tone
        ? PROMOTION_GROUP_SELECTED[tone]
        : { ring: 'ring-brand-accent', fill: 'border-brand-accent bg-brand-accent' };
    return (
        <button
            type="button"
            role={role}
            aria-checked={checked}
            disabled={disabled}
            onClick={onSelect}
            className={cn(
                'flex w-full items-start gap-2 rounded-lg border bg-card px-2.5 py-2 text-left transition-colors',
                checked
                    ? cn('border-transparent ring-2 ring-inset', selected.ring)
                    : pending
                      ? 'border-warning'
                      : 'border-border',
                disabled ? 'cursor-not-allowed' : 'hover:border-brand-accent/60',
                disabled && !checked && 'opacity-50',
            )}
        >
            <span
                aria-hidden
                className={cn(
                    'mt-0.5 flex size-3.5 flex-none items-center justify-center border',
                    role === 'radio' ? 'rounded-full' : 'rounded-[3px]',
                    checked ? cn(selected.fill, 'text-white') : 'border-border',
                )}
            >
                {checked && role === 'checkbox' && <Check className="size-3" />}
                {checked && role === 'radio' && <span className="size-1.5 rounded-full bg-white" />}
            </span>
            <span className="min-w-0 flex-1 text-[11px]">{children}</span>
        </button>
    );
}

/** «Ninguna» o «Sin regalo»: un renglón más, del mismo alto que los de promo. */
export function NoneChoiceRow({
    label,
    checked,
    disabled,
    onSelect,
    tone,
}: {
    label: string;
    checked: boolean;
    disabled: boolean;
    onSelect: () => void;
    tone?: PromotionGroupTone;
}) {
    return (
        <SelectablePromotionRow
            role="radio"
            checked={checked}
            disabled={disabled}
            onSelect={onSelect}
            {...(tone && { tone })}
        >
            <PromotionGroupRow icon={Ban} {...(tone && { tone })} name={label} />
        </SelectablePromotionRow>
    );
}
